/**
 * src/services/api.ts
 * Configuration Axios — Version corrigee P0
 * FIX : URL relative /api/v1 pour passer par le proxy Vite
 * VULN-038 : withCredentials: true pour cookies httpOnly
 * VULN-001 : Plus de stockage localStorage pour les tokens
 */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import * as formSnapshot from '@/hooks/formSnapshot'

// FIX : URL relative — Vite redirige /api vers le backend via proxy
// Cela evite les problemes CORS et SameSite=Lax cross-origin

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api/v1'
// ═══════════════════════════════════════════════════════════════
// SESSION PERSISTANTE — keep-alive + refresh silencieux + verrou
// Règles métier :
//  - Activité souris/clavier → la session ne doit JAMAIS expirer
//    (refresh silencieux toutes les 4 min tant qu'il y a de l'activité,
//    y compris au retour sur l'onglet).
//  - Aucune activité pendant 30 min → la session se VERROUILLE
//    (pas de perte : la page cible et les formulaires sont sauvegardés,
//    l'utilisateur retrouve tout après re-connexion).
//  - Multi-onglets : l'activité d'un onglet compte pour tous.
// ═══════════════════════════════════════════════════════════════
const INACTIVITY_LOCK_MS = 30 * 60 * 1000 // 30 minutes sans activité → verrou
let lastActivity = Date.now()
let refreshInFlight: Promise<boolean> | null = null
let keepAliveTick = 0

// Canal multi-onglets : une activité dans UN onglet réveille TOUS les onglets
const activityChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('lybibal-activity') : null
if (activityChannel) {
  activityChannel.onmessage = (e) => {
    const ts = Number(e.data)
    if (!isNaN(ts) && ts > lastActivity) lastActivity = ts
  }
}

/** Sauvegarde la page courante pour y revenir après re-connexion. */
function saveRedirectTarget() {
  try {
    sessionStorage.setItem('redirect_after_login', JSON.stringify({
      path: window.location.pathname + window.location.search,
      ts: Date.now(),
    }))
  } catch { /* sessionStorage indisponible */ }
}

/**
 * Verrouille la session SANS perte de données : la page courante et le
 * contenu des formulaires sont sauvegardés, puis l'utilisateur est renvoyé
 * vers /login?locked=1 où il retrouvera tout après re-connexion.
 * reason = 'inactivity' (30 min sans activité) | 'expired' (refresh impossible).
 */
function lockSession(reason: 'inactivity' | 'expired') {
  const path = window.location.pathname + window.location.search
  saveRedirectTarget()
  try { (formSnapshot as any).captureFormSnapshot?.() } catch { /* ignore */ }
  try { sessionStorage.setItem('lybibal-lock-reason', reason) } catch { /* ignore */ }
  useAuthStore.getState().logout()
  // Le redirect est aussi passé en paramètre URL : filet de sécurité si le
  // sessionStorage a été vidé entre-temps (navigateur, autre onglet…)
  window.location.href = `/login?locked=1&reason=${reason}&redirect=${encodeURIComponent(path)}`
}

/**
 * À appeler dans LoginPage APRÈS un login réussi :
 * retourne la page où se trouvait l'utilisateur (ou null) et restaure
 * automatiquement le contenu des formulaires sauvegardés.
 */
export function consumePostLoginRedirect(): string | null {
  // 1) Cible sauvegardée au verrouillage (sessionStorage)
  let path: string | null = null
  try {
    const raw = sessionStorage.getItem('redirect_after_login')
    sessionStorage.removeItem('redirect_after_login')
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.path && Date.now() - (parsed.ts || 0) <= 24 * 60 * 60 * 1000) {
        path = parsed.path
      }
    }
  } catch { /* sessionStorage indisponible */ }

  // 2) Filet de sécurité : paramètre URL (?redirect=/la/page)
  if (!path && typeof window !== 'undefined') {
    try {
      const q = new URLSearchParams(window.location.search).get('redirect')
      if (q && q.startsWith('/')) path = q
    } catch { /* ignore */ }
  }

  // 3) Dernière page visitée (sauvegarde périodique — sortie via route guard)
  if (!path) {
    try {
      const last = sessionStorage.getItem('lybibal-last-path')
      if (last && last.startsWith('/')) path = last
      sessionStorage.removeItem('lybibal-last-path')
    } catch { /* ignore */ }
  }

  // 3) Restauration des formulaires APRÈS le montage de la page cible
  // (si on restaure avant la navigation, les champs n'existent pas encore)
  if (path) {
    setTimeout(() => {
      try { (formSnapshot as any).restoreFormSnapshot?.() } catch { /* ignore */ }
    }, 400)
  }
  return path
}

/** Motif du dernier verrouillage ('inactivity' | 'expired' | null) — pour LoginPage. */
export function consumeLockReason(): string | null {
  try {
    const r = sessionStorage.getItem('lybibal-lock-reason')
    sessionStorage.removeItem('lybibal-lock-reason')
    return r
  } catch {
    return null
  }
}

/**
 * Rafraîchit le cookie de session UNE SEULE FOIS, même si plusieurs
 * requêtes 401 arrivent simultanément (single-flight).
 * skip401Handling évite la boucle : le refresh lui-même n'est pas re-intercepté.
 */
function tryRefresh(client: AxiosInstance): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = client
      .post('/auth/refresh', {}, { skip401Handling: true } as any)
      .then(() => true)
      .catch(() => false)
      .finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

class ApiService {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
      // VULN-038 CORRIGE : Cookies httpOnly envoyes automatiquement
      withCredentials: true,
    })
    this.setupInterceptors()

    // ─── KEEP-ALIVE + VERROUILLAGE INACTIVITÉ ───
    if (typeof window !== 'undefined') {
      const bumpActivity = () => {
        lastActivity = Date.now()
        try { activityChannel?.postMessage(lastActivity) } catch { /* canal fermé */ }
      }
      ;['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach(ev =>
        window.addEventListener(ev, bumpActivity, { passive: true })
      )

      // Au retour sur l'onglet : activité détectée + refresh immédiat
      // (corrige l'expiration "pendant que je travaille" quand l'onglet
      // passe en arrière-plan : l'interval était suspendu par le navigateur)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return
        bumpActivity()
        const auth: any = useAuthStore.getState()
        if (auth?.user || auth?.isAuthenticated) tryRefresh(this.client)
      })

      // Sauvegarde la dernière page visitée toutes les 5 s (filet de sécurité
      // pour le retour après login quand la sortie vient du route guard sans 401)
      setInterval(() => {
        try {
          const p = window.location.pathname
          if (p && p !== '/login') sessionStorage.setItem('lybibal-last-path', p + window.location.search)
        } catch { /* ignore */ }
      }, 5000)

      // Toutes les minutes : verrouiller si 30 min sans activité,
      // sinon refresh silencieux toutes les 4 min tant que l'onglet est visible
      setInterval(() => {
        const auth: any = useAuthStore.getState()
        if (!auth?.user && !auth?.isAuthenticated) return

        if (Date.now() - lastActivity > INACTIVITY_LOCK_MS) {
          lockSession('inactivity')
          return
        }

        keepAliveTick++
        if (document.visibilityState === 'visible' && keepAliveTick % 4 === 0) {
          tryRefresh(this.client)
        }
      }, 60 * 1000)
    }
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        // Le token JWT est dans le cookie httpOnly — pas d'injection manuelle
        return config
      },
      (error) => Promise.reject(error)
    )
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const status = error.response?.status
        const config: any = error.config || {}

        // 401 → on tente d'abord un refresh silencieux du cookie (une seule fois)
        if (
          status === 401 &&
          !config.skip401Handling &&
          !config._retry &&
          !String(config.url || '').includes('/auth/login')
        ) {
          const refreshed = await tryRefresh(this.client)
          if (refreshed) {
            config._retry = true
            return this.client(config) // cookie renouvelé → on rejoue la requête d'origine
          }
          // Refresh impossible → verrouillage avec sauvegarde complète
          // (page + formulaires) : l'utilisateur retrouve tout après re-connexion
          lockSession('expired')
          return Promise.reject(error)
        }

        const message = (error.response?.data as any)?.detail || error.message || 'Erreur serveur'
        if (!config.skip401Handling && message !== 'Token d\'authentification manquant') {
          toast.error(message)
        }
        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response.data
  }
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config)
    return response.data
  }
  async upload<T>(url: string, file: File, fieldName = 'file'): Promise<T> {
    const formData = new FormData()
    formData.append(fieldName, file)
    const response = await this.client.post<T>(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  // ... toutes les methodes existantes conservees ...
  recordPageVisit = (data: any) => this.post<any>('/audit/page-visits', data)
  getAll = (params?: Record<string, any>) => this.get<any>('/timetable', { params })
  createTimetableSlot = (data: any) => this.post<any>('/timetable', data)
  updateTimetableSlot = (id: number, data: any) => this.put<any>(`/timetable/${id}`, data)
  deleteTimetableSlot = (id: number) => this.delete<any>(`/timetable/${id}`)
  getTeacherSubjects = (teacherId: number) => this.get<any>(`/timetable/teacher-subjects/${teacherId}`)
  getClassSubjects = (classId: number) => this.get<any>(`/timetable/class-subjects/${classId}`)
  // ✅ Matières d'UNE classe avec les coefficients de la classe (subject_classes)
  getClassSubjectsWithCoefficients = (classId: number) => this.get<any>(`/classes/${classId}/subjects`)
  getSubjectClasses = (params?: Record<string, any>) => this.get<any>('/subject-classes', { params })
  getSubjects = (params?: Record<string, any>) => this.get<any>('/subjects', { params })
  createSubject = (data: any) => this.post<any>('/subjects', data)
  getClassrooms = () => this.get<any>('/rooms')
  createClassroom = (data: any) => this.post<any>('/rooms', data)
  getTeachers = (params?: Record<string, any>) => this.get<any>('/teachers', { params })
  getTeacherAssignments = (params?: Record<string, any>) => this.get<any>('/teachers/assignments', { params })
  createTeacherAssignment = (data: any) => this.post<any>('/teachers/assignments', data)
  getClasses = (params?: Record<string, any>) => this.get<any>('/classes', { params })
  getStudents = (params?: Record<string, any>) => this.get<any>('/students', { params })
  getGrades = (params?: Record<string, any>) => this.get<any>('/grades', { params })
  createGrade = (data: any) => this.post<any>('/grades', data)
  createBulkGrades = (data: any) => this.post<any>('/grades/bulk', data)
  updateGrade = (id: number, data: any) => this.put<any>(`/grades/${id}`, data)
  validateGrades = (gradeIds: number[]) => this.post<any>('/grades/validate', { grade_ids: gradeIds })
  getGradeSequences = () => this.get<any>('/grades/sequences')
  createGradeSequence = (data: any) => this.post<any>('/grades/sequences', data)
  toggleSequence = (id: number) => this.patch<any>(`/grades/sequences/${id}/toggle`, {})
  getClassRanking = (classId: number, params?: Record<string, any>) => this.get<any>(`/grades/ranking/${classId}`, { params })
  getHonorBoard = (params?: Record<string, any>) => this.get<any>('/grades/honor-board', { params })
  getClassAverage = (classId: number, params?: Record<string, any>) => this.get<any>(`/grades/averages/${classId}`, { params })
  calculateAverages = (evaluationPeriodId: number, params?: Record<string, any>) => this.post<any>(`/grades/calculate-averages/${evaluationPeriodId}`, null, { params })
  getGradeStatistics = (classId: number, subjectId: number, sequenceId: number) => this.get<any>(`/grades/statistics/${classId}/${subjectId}/${sequenceId}`)
  generateBulletin = (studentId: number, params?: Record<string, any>) => this.get<Blob>(`/grades/bulletin/${studentId}`, { params, responseType: 'blob' })
  getGradeClasses = (params?: Record<string, any>) => this.get<any>('/grades/classes', { params })
  getGradeSubjects = (params?: Record<string, any>) => this.get<any>('/grades/subjects', { params })
  getGradeSubjectsStatus = (params?: Record<string, any>) => this.get<any>('/grades/subjects-status', { params })
  getGradePeriods = (params?: Record<string, any>) => this.get<any>('/grades/periods', { params })
  importGrades = (formData: FormData) => this.post<any>('/grades/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  getFarms = (params?: Record<string, any>) => this.get<any>('/farms', { params })
  getFarmById = (id: number) => this.get<any>(`/farms/${id}`)
  createFarm = (data: any) => this.post<any>('/farms', data)
  updateFarm = (id: number, data: any) => this.put<any>(`/farms/${id}`, data)
  deleteFarm = (id: number) => this.delete<any>(`/farms/${id}`)
  getLivestock = (params?: Record<string, any>) => this.get<any>('/livestock', { params })
  getAnimalById = (id: number) => this.get<any>(`/livestock/${id}`)
  createAnimal = (data: any) => this.post<any>('/livestock', data)
  updateAnimal = (id: number, data: any) => this.put<any>(`/livestock/${id}`, data)
  deleteAnimal = (id: number) => this.delete<any>(`/livestock/${id}`)
  getInventory = (params?: Record<string, any>) => this.get<any>('/inventory', { params })
  getPurchases = (params?: Record<string, any>) => this.get<any>('/purchases', { params })
  getSales = (params?: Record<string, any>) => this.get<any>('/sales', { params })
  getProjects = (params?: Record<string, any>) => this.get<any>('/projects', { params })
  getReports = (params?: Record<string, any>) => this.get<any>('/reports', { params })
  getBudgets = (params?: Record<string, any>) => this.get<any>('/budgets', { params })
  getBudgetById = (id: number) => this.get<any>(`/budgets/${id}`)
  initializeBudget = (data: any) => this.post<any>('/budgets/initialize', data)
  createMainLine = (data: any) => this.post<any>('/budgets/main-lines', data)
  createSubLine = (data: any) => this.post<any>('/budgets/sub-lines', data)
  createExpense = (data: any) => this.post<any>('/budgets/expenses', data)
  validateExpense = (id: number, data?: any) => this.post<any>(`/budgets/expenses/${id}/validate`, data)
  approveBudget = (id: number, data?: any) => this.post<any>(`/budgets/${id}/approve`, data)
  getTimetable = (params?: Record<string, any>) => this.get<any>('/timetable', { params })
  getDisciplineRecords = (params?: Record<string, any>) => this.get<any>('/discipline', { params })
  getNews = (params?: Record<string, any>) => this.get<any>('/news', { params })
  getEvents = (params?: Record<string, any>) => this.get<any>('/events', { params })
  getAuditLogs = (params?: Record<string, any>) => this.get<any>('/audit/logs', { params })
  getCompetencies = (params?: Record<string, any>) => this.get<any>('/competencies', { params })
  createCompetency = (data: any) => this.post<any>('/competencies', data)
  updateCompetency = (id: number, data: any) => this.put<any>(`/competencies/${id}`, data)
  deleteCompetency = (id: number) => this.delete<any>(`/competencies/${id}`)
  bulkCreateCompetencies = (data: any) => this.post<any>('/competencies/bulk', data)
  getStudentBulletin = (studentId: number, params?: Record<string, any>) => this.get<any>(`/bulletins/student/${studentId}`, { params })
  getClassBulletins = (classId: number, params?: Record<string, any>) => this.get<any>(`/bulletins/class/${classId}`, { params })
}

export const apiService = new ApiService()
export default apiService

// ============================================
// AUTH
// ============================================
export const authApi = {
  login: (email: string, password: string) =>
    apiService.post<any>('/auth/login', { email, password }),
  loginWith2FA: (email: string, password: string, totp_code: string) =>
    apiService.post<any>('/auth/login/2fa', { email, password, totp_code }),
  loginWithEmail2FA: (email: string, password: string, email_otp: string) =>
    apiService.post<any>('/auth/login/email-2fa', { email, password, email_otp }),
  logout: () => apiService.post('/auth/logout', {}),
  me: () => apiService.get<any>('/auth/me'),
  refresh: () => apiService.post<{ message: string }>('/auth/refresh'),
  changePassword: (oldPassword: string, newPassword: string) =>
    apiService.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),

  getUsers: (params?: any) => apiService.get<any>('/auth/users', { params }),
  getUserById: (id: number) => apiService.get<any>(`/auth/users/${id}`),
  createUser: (data: any) => apiService.post<any>('/auth/users', data),
  updateUser: (id: number, data: any) => apiService.put<any>(`/auth/users/${id}`, data),
  deleteUser: (id: number) => apiService.delete<any>(`/auth/users/${id}`),

  setup2FA: () => apiService.post<any>('/auth/2fa/setup', {}),
  enable2FA: (data: any) => apiService.post<any>('/auth/2fa/enable', data),
  disable2FA: (data: any) => apiService.post<any>('/auth/2fa/disable', data),
  get2FAStatus: () => apiService.get<any>('/auth/2fa/status'),

  getEmail2FAStatus: () => apiService.get<any>('/auth/2fa/email/status'),
  setupEmail2FA: () => apiService.post<any>('/auth/2fa/email/setup', {}),
  enableEmail2FA: (data: any) => apiService.post<any>('/auth/2fa/email/enable', data),
  disableEmail2FA: (data: any) => apiService.post<any>('/auth/2fa/email/disable', data),

  getUserPermissions: (userId: number) => apiService.get<any>(`/auth/users/${userId}/permissions`),

  // ─── ADMIN 2FA ───
  adminDisable2FA: (userId: number) =>
    apiService.post<any>(`/auth/users/${userId}/2fa/disable`, {}),
  adminEnable2FA: (userId: number) =>
    apiService.post<any>(`/auth/users/${userId}/2fa/enable`, {}),
  adminDisableEmail2FA: (userId: number) =>
    apiService.post<any>(`/auth/users/${userId}/email-2fa/disable`, {}),
  adminEnableEmail2FA: (userId: number) =>
    apiService.post<any>(`/auth/users/${userId}/email-2fa/enable`, {}),
}

// ... (tous les autres exports API conserves)
// ============================================
// ÉLÈVES
// ============================================
export const studentsApi = {
  getAll: (params?: any) => apiService.get<any>('/students', { params }),
  getStats: (params?: any) => apiService.get<any>('/students/stats/dashboard', {params}),
  getById: (id: number) => apiService.get<any>(`/students/${id}`),
  create: (data: any) => apiService.post<any>('/students', data),
  update: (id: number, data: any) => apiService.put<any>(`/students/${id}`, data),
  delete: (id: number) => apiService.delete(`/students/${id}`),
  importStudents: (formData: FormData) => apiService.post<any>('/students/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadPhoto: (file: File) => apiService.upload<any>('/students/upload-photo', file),
  exportExcel: (params?: any) => apiService.get<Blob>('/students/export/excel', { params, responseType: 'blob' }),
  getContactsTemplate: (params?: { class_id?: number; school_id?: number; academic_year_id?: number }) =>
    apiService.get<Blob>('/students/bulk/contacts-template', { params, responseType: 'blob' }),
  importContacts: (formData: FormData) => apiService.post<any>('/students/bulk/update-contacts', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  transfer: (id: number, data: any) => apiService.post<any>(`/students/${id}/transfer`, data),
}

// ... (tous les autres exports conservés)
// ============================================
// ENSEIGNANTS
// ============================================
export const teachersApi = {
  getAll: (params?: { status?: string; contract_type?: string; search?: string; }) =>
    apiService.get<any>('/teachers', { params }),
  getById: (id: number) => apiService.get<any>(`/teachers/${id}`),
  create: (data: any) => apiService.post<any>('/teachers', data),
  update: (id: number, data: any) => apiService.put<any>(`/teachers/${id}`, data),
  delete: (id: number) => apiService.delete(`/teachers/${id}`),
  getSubjects: (params?: { section_id?: number; specialty_id?: number; status?: string }) => {
    const cleanParams: Record<string, any> = {}
    if (params?.section_id !== undefined && params.section_id !== null) cleanParams.section_id = params.section_id
    if (params?.specialty_id !== undefined && params.specialty_id !== null) cleanParams.specialty_id = params.specialty_id
    if (params?.status !== undefined && params.status !== null && params.status !== '') cleanParams.status = params.status
    return apiService.get<any>('/subjects', Object.keys(cleanParams).length > 0 ? { params: cleanParams } : undefined)
  },
  createSubject: (data: any) => apiService.post<any>('/subjects', data),
  getAssignments: (params?: { teacher_id?: number; class_id?: number; academic_year_id?: number }) => {
    const cleanParams: Record<string, any> = {}
    if (params?.teacher_id !== undefined) cleanParams.teacher_id = params.teacher_id
    if (params?.class_id !== undefined) cleanParams.class_id = params.class_id
    if (params?.academic_year_id !== undefined) cleanParams.academic_year_id = params.academic_year_id
    return apiService.get<any>('/teachers/assignments', { params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined })
  },
  createAssignment: (data: any) => apiService.post<any>('/teachers/assignments', data),
  deleteAssignment: (assignmentId: number) => apiService.delete(`/teachers/assignments/${assignmentId}`),
  getDelays: (id: number, params?: Record<string, any>) => apiService.get<any>(`/teachers/${id}/delays`, { params }),
  recordDelay: (data: any) => apiService.post('/teachers/delays', data),
}

// ============================================
// CLASSES & NIVEAUX
// ============================================
export const classesApi = {
  getAll: (params?: Record<string, any>) => apiService.get<any>('/classes', { params }),
  getById: (id: number) => apiService.get<any>(`/classes/${id}`),
  create: (data: any) => apiService.post<any>('/classes', data),
  update: (id: number, data: any) => apiService.put<any>(`/classes/${id}`, data),
  delete: (id: number) => apiService.delete(`/classes/${id}`),
  getStudents: (id: number) => apiService.get<any>(`/classes/${id}/students`),
  getSubjects: (id: number) => apiService.get<any>(`/classes/${id}/subjects`),
}

export const levelsApi = {
  getAll: (params?: Record<string, any>) => apiService.get<any>('/levels', { params }),
  getById: (id: number) => apiService.get<any>(`/levels/${id}`),
  create: (data: any) => apiService.post<any>('/levels', data),
  update: (id: number, data: any) => apiService.put<any>(`/levels/${id}`, data),
  delete: (id: number) => apiService.delete(`/levels/${id}`),
}

export const sectionsApi = {
  getAll: (params?: Record<string, any>) => apiService.get<any>('/sections', { params }),
  getById: (id: number) => apiService.get<any>(`/sections/${id}`),
  create: (data: any) => apiService.post<any>('/sections', data),
  update: (id: number, data: any) => apiService.put<any>(`/sections/${id}`, data),
  delete: (id: number) => apiService.delete(`/sections/${id}`),
}

// ============================================
// MATIÈRES
// ============================================
export const subjectsApi = {
  getAll: (params?: { section_id?: number; specialty_id?: number; status?: string }) => {
    const cleanParams: Record<string, any> = {}
    if (params?.section_id !== undefined && params.section_id !== null) cleanParams.section_id = params.section_id
    if (params?.specialty_id !== undefined && params.specialty_id !== null) cleanParams.specialty_id = params.specialty_id
    if (params?.status !== undefined && params.status !== null && params.status !== '') cleanParams.status = params.status
    return apiService.get<any>('/subjects', Object.keys(cleanParams).length > 0 ? { params: cleanParams } : undefined)
  },
  getById: (id: number) => apiService.get<any>(`/subjects/${id}`),
  create: (data: any) => apiService.post<any>('/subjects', data),
  update: (id: number, data: any) => apiService.put<any>(`/subjects/${id}`, data),
  delete: (id: number) => apiService.delete(`/subjects/${id}`),
  assignSubjectAP: (subjectId: number, teacherId: number | null) =>
    apiService.patch<any>(`/teachers/subjects/${subjectId}/assign-ap`, { teacher_id: teacherId }),
}

export const subjectClassesApi = {
  getAll: (params?: any) => apiService.get<any>('/subject-classes', { params }),
  getById: (id: number) => apiService.get<any>(`/subject-classes/${id}`),
  create: (data: any) => apiService.post<any>('/subject-classes', data),
  update: (id: number, data: any) => apiService.put<any>(`/subject-classes/${id}`, data),
  delete: (id: number) => apiService.delete(`/subject-classes/${id}`),
  duplicate: (data: { source_class_id: number; target_class_id: number; school_id?: number; academic_year_id?: number; overwrite_existing?: boolean }) =>
    apiService.post<any>('/subject-classes/duplicate', data),
  bulkByLevel: (data: { subject_id: number; level_id: number; coefficient: number; hours_per_week: number; status: string; school_id?: number; academic_year_id?: number; overwrite_existing?: boolean }) =>
    apiService.post<any>('/subject-classes/bulk-by-level', data),
}

// ============================================
// EMPLOI DU TEMPS
// ============================================
export const timetableApi = {
  getAll: (params?: any) => apiService.get<any>('/timetable', { params }),
  getById: (id: number) => apiService.get<any>(`/timetable/${id}`),
  create: (data: any) => apiService.post<any>('/timetable', data),
  update: (id: number, data: any) => apiService.put<any>(`/timetable/${id}`, data),
  delete: (id: number) => apiService.delete(`/timetable/${id}`),
  getTimeSlots: () => apiService.get<any>('/timetable/time-slots'),
  createTimeSlot: (data: any) => apiService.post<any>('/timetable/time-slots', data),
  getTeacherSubjects: (teacherId: number) => apiService.get<any>(`/timetable/teacher-subjects/${teacherId}`),
  getClassSubjects: (classId: number) => apiService.get<any>(`/timetable/class-subjects/${classId}`),
  getTeacherTimetable: (teacherId: number, params?: any) => apiService.get<any>(`/teachers/${teacherId}/timetable`, { params }),
  getClassTimetable: (classId: number, params?: any) => apiService.get<any>(`/classes/${classId}/timetable`, { params }),
}

// ============================================
// NOTES / GRADES
// ============================================
export const gradesApi = {
  getSequences: () => apiService.get<any>('/grades/sequences'),
  createSequence: (data: any) => apiService.post<any>('/grades/sequences', data),
  toggleSequence: (id: number) => apiService.patch<any>(`/grades/sequences/${id}/toggle`, {}),
  getGrades: (params?: any) => apiService.get<any>('/grades', { params }),
  createGrade: (data: any) => apiService.post<any>('/grades', data),
  createBulkGrades: (data: any) => apiService.post<any>('/grades/bulk', data),
  updateGrade: (id: number, data: any) => apiService.put<any>(`/grades/${id}`, data),
  validateGrades: (gradeIds: number[]) => apiService.post<any>('/grades/validate', { grade_ids: gradeIds }),
  getClassAverage: (classId: number, params?: any) => apiService.get<any>(`/grades/averages/${classId}`, { params }),
  getClassRanking: (classId: number, params?: any) => apiService.get<any>(`/grades/ranking/${classId}`, { params }),
  getHonorBoard: (params?: any) => apiService.get<any>('/grades/honor-board', { params }),
  calculateAverages: (evaluationPeriodId: number, params?: any) => apiService.post<any>(`/grades/calculate-averages/${evaluationPeriodId}`, null, { params }),
  getGradeStatistics: (classId: number, subjectId: number, sequenceId: number) => apiService.get<any>(`/grades/statistics/${classId}/${subjectId}/${sequenceId}`),
  generateBulletin: (studentId: number, params?: any) => apiService.get<Blob>(`/grades/bulletin/${studentId}`, { params, responseType: 'blob' }),
  getConfig: (params?: any) => apiService.get<any>('/grades/config', { params }),
  openEntry: (data: any) => apiService.post('/grades/config/open', data),
  closeEntry: (configId: number) => apiService.post(`/grades/config/${configId}/close`, {}),
  getStudentGrades: (studentId: number, params?: any) => apiService.get<any>(`/students/${studentId}/grades`, { params }),
  enterGrades: (data: any) => apiService.post('/grades/bulk', data),
  publish: (configId: number) => apiService.post(`/grades/config/${configId}/publish`, {}),
  getAverages: (params?: any) => apiService.get<any>('/grades/averages', { params }),
  getStudentAverage: (studentId: number, params?: any) => apiService.get<any>(`/students/${studentId}/average`, { params }),
  getHonorRollConfig: (params?: any) => apiService.get<any>('/grades/honor-roll-config', { params }),
  updateHonorRollConfig: (id: number, data: any) => apiService.put<any>(`/grades/honor-roll-config/${id}`, data),
  getClasses: (params?: Record<string, any>) => apiService.get<any>('/grades/classes', { params }),
  getSubjectsByClass: (params?: Record<string, any>) => apiService.get<any>('/grades/subjects', { params }),
  getSubjectsStatus: (params?: Record<string, any>) => apiService.get<any>('/grades/subjects-status', { params }),
  getPeriods: (params?: Record<string, any>) => apiService.get<any>('/grades/periods', { params }),
  importGrades: (formData: FormData) => apiService.post<any>('/grades/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
}

// ============================================
// DISCIPLINE
// ============================================
export const disciplineApi = {
  getAll: (params?: any) => apiService.get<any>('/discipline', { params }),
  getByStudent: (studentId: number) => apiService.get<any>(`/students/${studentId}/discipline`),
  create: (data: any) => apiService.post<any>('/discipline', data),
  update: (id: number, data: any) => apiService.put<any>(`/discipline/${id}`, data),
  delete: (id: number) => apiService.delete(`/discipline/${id}`),
  getStats: (params?: any) => apiService.get<any>('/discipline/stats', { params }),
  getStudentStats: (studentId: number) => apiService.get<any>(`/students/${studentId}/discipline-stats`),
  createConvocation: (data: any) => apiService.post<any>('/discipline/convocations', data),
  getConvocations: (params?: any) => apiService.get<any>('/discipline/convocations', { params }),
  bulkAbsences: (payload: any) => apiService.post('/discipline/bulk-absences', payload),
  justify: (id: number, data: any) => apiService.put(`/discipline/${id}/justify`, data),
  recordConvocation: (id: number) => apiService.post(`/discipline/${id}/convocation`),
}

// ============================================
// BUDGET
// ============================================
export const budgetApi = {
  getCategories: () => apiService.get<any>('/budget/categories'),
  createCategory: (data: any) => apiService.post<any>('/budget/categories', data),
  updateCategory: (id: number, data: any) => apiService.put<any>(`/budget/categories/${id}`, data),
  deleteCategory: (id: number) => apiService.delete(`/budget/categories/${id}`),
  getSubLines: (categoryId?: number) => apiService.get<any>('/budget/sub-lines', { params: categoryId ? { category_id: categoryId } : undefined }),
  updateSubLine: (id: number, data: any) => apiService.put<any>(`/budget/sub-lines/${id}`, data),
  deleteSubLine: (id: number) => apiService.delete(`/budget/sub-lines/${id}`),
  getSummary: (params?: any) => apiService.get<any>('/budget/summary', { params }),
  exportPDF: (params?: any) => apiService.get<Blob>('/budget/export/pdf', { params, responseType: 'blob' }),

  getBudgets: (params?: { page?: number; per_page?: number; school_id?: number; academic_year_id?: number; status?: string }) =>
    apiService.get<any>('/budget', { params }),

  getBudgetById: (id: number) =>
    apiService.get<any>(`/budget/${id}`),

  getBudgetFull: (id: number) =>
    apiService.get<any>(`/budget/${id}/full`),

  initializeBudget: (data: any) =>
    apiService.post<any>('/budget/initialize', data),

  approveBudget: (id: number, data?: { notes?: string }) =>
    apiService.post<any>(`/budget/${id}/approve`, data),

  // ─── MAIN LINES ───
  createMainLine: (budgetYearId: number, data: any) =>
    apiService.post<any>(`/budget/${budgetYearId}/main-lines`, data),

  deleteMainLine: (id: number) =>
    apiService.delete<any>(`/budget/main-lines/${id}`),

  // ─── SUB LINES ───
  createSubLine: (mainLineId: number, data: any) =>
    apiService.post<any>(`/budget/main-lines/${mainLineId}/sub-lines`, data),

  // ─── EXPENSES ───
  getExpenses: (budgetYearId: number, params?: { page?: number; per_page?: number; main_line_id?: number; search?: string }) =>
    apiService.get<any>(`/budget/${budgetYearId}/expenses`, { params }),

  createExpense: (budgetYearId: number, data: any) =>
    apiService.post<any>(`/budget/${budgetYearId}/expenses`, data),

  validateExpense: (expenseId: number) =>
    apiService.post<any>(`/budget/expenses/${expenseId}/validate`, {}),

  deleteExpense: (id: number) =>
    apiService.delete<any>(`/budget/expenses/${id}`),

  // ─── REPORTS ───
  printBudgetInitial: (budgetYearId: number) =>
    apiService.get<any>(`/budget/${budgetYearId}/print/initial`),

  exportExcel: (budgetYearId: number) =>
    apiService.get<any>(`/budget/${budgetYearId}/export/excel`),

  getStatistics: (budgetYearId: number) =>
    apiService.get<any>(`/budget/${budgetYearId}/statistics`),

  getBalanceSheet: (budgetYearId: number) =>
    apiService.get<any>(`/budget/${budgetYearId}/balance-sheet`),
}

// ============================================
// STATISTIQUES
// ============================================
export const statsApi = {
  getDashboard: () => apiService.get<any>('/stats/dashboard'),
  getTermResults: (params?: any) => apiService.get<any>('/stats/term-results', { params }),
  getClassResults: (classId: number, params?: any) => apiService.get<any>(`/stats/classes/${classId}/results`, { params }),
  getStudentRanking: (params?: any) => apiService.get<any>('/stats/ranking', { params }),
  getPassRate: (params?: any) => apiService.get<any>('/stats/pass-rate', { params }),
  getHonorRoll: (params?: any) => apiService.get<any>('/stats/honor-roll', { params }),
  getClassCouncil: (classId: number, params?: any) => apiService.get<any>(`/stats/classes/${classId}/council`, { params }),
  generatePV: (data: any) => apiService.post<Blob>('/stats/pv', data, { responseType: 'blob' }),
  getAdmittedList: (params?: any) => apiService.get<any>('/stats/admitted', { params }),
  getRepeatList: (params?: any) => apiService.get<any>('/stats/repeat', { params }),
  getExcludedList: (params?: any) => apiService.get<any>('/stats/excluded', { params }),
  getRecentActivity: (params?: any) => apiService.get<any>('/stats/recent-activity', { params }),
}

// ============================================
// COMMUNICATION (MESSAGES) — V2 avec suppression
// ============================================
export const messagesApi = {
  getTemplates: () => apiService.get<any>('/messages/templates'),
  createTemplate: (data: any) => apiService.post<any>('/messages/templates', data),
  updateTemplate: (id: number, data: any) => apiService.put<any>(`/messages/templates/${id}`, data),
  deleteTemplate: (id: number) => apiService.delete(`/messages/templates/${id}`),
  sendSMS: (data: any) => apiService.post('/messages/sms', data),
  sendEmail: (data: any) => apiService.post('/messages/email', data),
  getSentMessages: (params?: any) => apiService.get<any>('/messages/logs', { params }),
  getSmsCredits: () => apiService.get<any>('/messages/sms-credits'),
  purchaseCredits: (amount: number) => apiService.post('/messages/sms-credits/purchase', { amount }),

  // ═══ SUPPRESSION HISTORIQUE ═══
  deleteMessageLog: (id: number) => apiService.delete<any>(`/messages/logs/${id}`),
  deleteMessageLogsBulk: (ids: number[]) => apiService.delete<any>('/messages/logs/bulk', { data: ids }),
  deleteAllMessageLogs: (params?: any) => apiService.delete<any>('/messages/logs/all', { params }),
}


// ============================================
// SPÉCIALITÉS & GROUPES DE SPÉCIALITÉ
// ============================================
export const specialtiesApi = {
  getSpecialtyGroups: (sectionId?: number) =>
    apiService.get<any[]>('/classes/specialty-groups', { params: sectionId ? { section_id: sectionId } : undefined }),
  createSpecialtyGroup: (data: any) => apiService.post<any>('/classes/specialty-groups', data),
  updateSpecialtyGroup: (id: number, data: any) => apiService.put<any>(`/classes/specialty-groups/${id}`, data),
  deleteSpecialtyGroup: (id: number) => apiService.delete(`/classes/specialty-groups/${id}`),
  getSpecialties: (groupId: number) => apiService.get<any[]>(`/classes/specialty-groups/${groupId}/specialties`),
  createSpecialty: (groupId: number, data: any) => apiService.post<any>(`/classes/specialty-groups/${groupId}/specialties`, data),
  updateSpecialty: (id: number, data: any) => apiService.put<any>(`/classes/specialty-groups/specialties/${id}`, data),
  deleteSpecialty: (id: number) => apiService.delete(`/classes/specialty-groups/specialties/${id}`),
  downloadTemplate: () => apiService.get<Blob>('/classes/specialty-groups/template', { responseType: 'blob' }),
  importFromExcel: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return apiService.post<any>('/classes/specialty-groups/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
}

// ============================================
// CYCLES
// ============================================
export const cyclesApi = {
  getAll: (params?: Record<string, any>) => apiService.get<any>('/cycles', { params }),
  getById: (id: number) => apiService.get<any>(`/cycles/${id}`),
  create: (data: any) => apiService.post<any>('/cycles', data),
  update: (id: number, data: any) => apiService.put<any>(`/cycles/${id}`, data),
  delete: (id: number) => apiService.delete(`/cycles/${id}`),
}

// ============================================
// ÉCOLE / STRUCTURE ACADÉMIQUE
// ============================================
export const schoolApi = {
  getSchools: () => apiService.get<any>('/schools'),
  getSchoolById: (id: number) => apiService.get<any>(`/schools/${id}`),
  createSchool: (data: any) => apiService.post<any>('/schools', data),
  updateSchool: (id: number, data: any) => apiService.put<any>(`/schools/${id}`, data),
  deleteSchool: (id: number) => apiService.delete(`/schools/${id}`),

  getAcademicYears: (schoolId: number) => apiService.get<any>(`/schools/${schoolId}/academic-years`),
  getCurrentAcademicYear: (schoolId: number) => apiService.get<any>(`/schools/${schoolId}/academic-years/current`),
  createAcademicYear: (schoolId: number, data: any) => apiService.post<any>(`/schools/${schoolId}/academic-years`, data),
  updateAcademicYear: (schoolId: number, yearId: number, data: any) =>
    apiService.put<any>(`/schools/${schoolId}/academic-years/${yearId}`, data),
  deleteAcademicYear: (schoolId: number, yearId: number) =>
    apiService.delete(`/schools/${schoolId}/academic-years/${yearId}`),
  setCurrentAcademicYear: (schoolId: number, yearId: number) =>
    apiService.post<any>(`/schools/${schoolId}/academic-years/${yearId}/set-current`, {}),

  getSections: (schoolId: number) => apiService.get<any>(`/schools/${schoolId}/sections`),
  createSection: (schoolId: number, data: any) => apiService.post<any>(`/schools/${schoolId}/sections`, data),

  getCycles: (sectionId: number) => apiService.get<any>(`/schools/sections/${sectionId}/cycles`),
  createCycle: (sectionId: number, data: any) => apiService.post<any>(`/schools/sections/${sectionId}/cycles`, data),

  getLevels: (cycleId: number) => apiService.get<any>(`/schools/cycles/${cycleId}/levels`),
  createLevel: (cycleId: number, data: any) => apiService.post<any>(`/schools/cycles/${cycleId}/levels`, data),

  getCompleteStructure: (schoolId?: number) => apiService.get<any>('/schools/structure/complete', { params: schoolId ? { school_id: schoolId } : undefined }),

  getSelectedAcademicYear: (schoolId: number) =>
    apiService.get<any>(`/schools/${schoolId}/academic-years/selected`),

  uploadLogo: (file: File, schoolId?: number) => {
    if (!schoolId) throw new Error('school_id requis pour uploader le logo')
    const formData = new FormData()
    formData.append('file', file)
    return apiService.post<{ logo_url: string; full_url: string; message: string }>(`/schools/${schoolId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  getLogo: (schoolId: number) => apiService.get<{ logo_url: string; full_url: string }>(`/schools/${schoolId}/logo`),

  getPermissions: () => apiService.get<any>('/auth/permissions'),

  setSelectedAcademicYear: (schoolId: number, yearId: number) =>
    apiService.post(`/schools/${schoolId}/academic-years/${yearId}/set-selected`),

  duplicateAcademicYear: (schoolId: number, data: any) =>
    apiService.post<any>(`/schools/${schoolId}/academic-years/duplicate`, data),
}

// ============================================
// FRAIS SCOLARITÉ
// ============================================
export const feesApi = {
  getFeeTypes: (params?: { institution_type?: string; category?: string; is_active?: boolean }) =>
    apiService.get<any>('/fees/types', { params }),
  getFeeTypeById: (id: number) => apiService.get<any>(`/fees/types/${id}`),
  createFeeType: (data: any) => apiService.post<any>('/fees/types', data),
  updateFeeType: (id: number, data: any) => apiService.put<any>(`/fees/types/${id}`, data),
  deleteFeeType: (id: number) => apiService.delete(`/fees/types/${id}`),
  seedDefaultTypes: (institutionType: string) =>
    apiService.post<any>(`/fees/seed-default-types?institution_type=${institutionType}`),

  getFeeStructures: (params?: {
    school_id?: number; academic_year_id?: number; level_id?: number;
    class_id?: number; section_id?: number; cycle_id?: number;
    fee_type_id?: number; institution_type?: string;
  }) => apiService.get<any>('/fees/structures', { params }),
  getFeeStructureById: (id: number) => apiService.get<any>(`/fees/structures/${id}`),
  createFeeStructure: (data: any) => apiService.post<any>('/fees/structures', data),
  createBulkFeeStructures: (dataList: any[]) => apiService.post<any>('/fees/structures/bulk', dataList),
  updateFeeStructure: (id: number, data: any) => apiService.put<any>(`/fees/structures/${id}`, data),
  deleteFeeStructure: (id: number) => apiService.delete(`/fees/structures/${id}`),

  getPayments: (params?: {
    student_id?: number; fee_structure_id?: number; class_id?: number;
    status?: string; academic_year_id?: number;
  }) => apiService.get<any>('/fees/payments', { params }),
  createPayment: (data: any) => apiService.post<any>('/fees/payments', data),
  createBulkPayments: (data: any) => apiService.post<any>('/fees/payments/bulk', data),
  createBulkPaymentsByType: (data: any) => apiService.post<any>('/fees/payments/bulk-by-type', data),
  updatePayment: (id: number, data: any) => apiService.put<any>(`/fees/payments/${id}`, data),
  deletePayment: (id: number) => apiService.delete(`/fees/payments/${id}`),

  getClassFeeStatus: (classId: number, academicYearId: number, feeTypeId?: number, institutionType?: string) =>
    apiService.get<any>(`/fees/reports/class-status?class_id=${classId}&academic_year_id=${academicYearId}${feeTypeId ? `&fee_type_id=${feeTypeId}` : ''}${institutionType ? `&institution_type=${institutionType}` : ''}`),
  getClassSummary: (classId: number, academicYearId: number) =>
    apiService.get<any>(`/fees/reports/class-summary?class_id=${classId}&academic_year_id=${academicYearId}`),
  exportFeeReport: (params: {
    class_id: number; academic_year_id: number; format?: string;
    fee_type_id?: number; status_filter?: string;
  }) => apiService.get<any>('/fees/reports/export', { params }),
}

export const budgetReportsApi = {
  generateConseil: (
    budgetYearId: number,
    data: {
      numero_rapport: number
      proviseur: string
      effectifs: {
        premier_cycle: number
        second_cycle: number
        total: number
      }
      date_signature?: string
      observations?: string
    }
  ) => apiService.post<any>(`/budget/${budgetYearId}/reports/conseil`, data),

  generateProjet: (
    budgetYearId: number,
    data: {
      effectifs: {
        premier_cycle: number
        second_cycle: number
        total: number
      }
      taux_contribution?: {
        premier_cycle: number
        second_cycle: number
      }
      previous_year_balance?: number
      date_signature?: string
    }
  ) => apiService.post<any>(`/budget/${budgetYearId}/reports/projet`, data),

  downloadReport: (budgetYearId: number, filename: string) =>
    apiService.get<Blob>(`/budget/${budgetYearId}/reports/download/${filename}`, {
      responseType: 'blob',
    }),
}

export const subjectSpecialtiesApi = {
  getAll: () => apiService.get('/subject-specialties'),
  create: (data: any) => apiService.post('/subject-specialties', data),
  update: (id: number, data: any) => apiService.put(`/subject-specialties/${id}`, data),
  delete: (id: number) => apiService.delete(`/subject-specialties/${id}`),
  duplicate: (data: any) => apiService.post('/subject-specialties/duplicate', data),
}

// ============================================
// ADMIN
// ============================================
export const adminApi = {
  getUsers: (params?: Record<string, any>) => apiService.get<any>('/auth/users', { params }),
  createUser: (data: any) => apiService.post<any>('/auth/users', data),
  updateUser: (id: number, data: any) => apiService.put<any>(`/auth/users/${id}`, data),
  deleteUser: (id: number) => apiService.delete(`/auth/users/${id}`),
  resetPassword: (id: number) => apiService.post(`/auth/users/${id}/reset-password`, {}),

  getRoles: () => apiService.get<any>('/auth/roles'),
  getRoleById: (id: number) => apiService.get<any>(`/auth/roles/${id}`),
  createRole: (data: any) => apiService.post<any>('/auth/roles', data),
  updateRole: (id: number, data: any) => apiService.put<any>(`/auth/roles/${id}`, data),
  deleteRole: (id: number) => apiService.delete(`/auth/roles/${id}`),

  getPermissions: () => apiService.get<any>('/auth/permissions'),
  getPermissionById: (id: number) => apiService.get<any>(`/auth/permissions/${id}`),
  createPermission: (data: any) => apiService.post<any>('/auth/permissions', data),
  updatePermission: (id: number, data: any) => apiService.put<any>(`/auth/permissions/${id}`, data),
  deletePermission: (id: number) => apiService.delete(`/auth/permissions/${id}`),

  getRolePermissions: (roleId: number) => apiService.get<any>(`/auth/roles/${roleId}/permissions`),
  grantPermission: (roleId: number, permissionId: number) =>
    apiService.post<any>(`/auth/roles/${roleId}/permissions`, { permission_id: permissionId }),
  revokePermission: (roleId: number, permissionId: number) =>
    apiService.delete<any>(`/auth/roles/${roleId}/permissions/${permissionId}`),

  updateUserPermissions: (userId: number, data: any) =>
    apiService.put<any>(`/auth/users/${userId}/permissions`, data),

  getAuditLogs: (params?: Record<string, any>) => apiService.get<any>('/audit/logs', { params }),
  getAuditStatistics: (days?: number) => apiService.get<any>(`/audit/statistics`, { params: { days: days || 30 } }),

  getAnalyticsDashboard: () => apiService.get<any>('/audit/dashboard'),
  getPageVisits2: (params?: Record<string, any>) => apiService.get<any>('/audit/page-visits', { params }),

  getSchool: () => apiService.get<any>('/admin/school'),
  updateSchool: (data: any) => apiService.put<any>('/admin/school', data),
  uploadLogo: (file: File, schoolId?: number) => {
    if (!schoolId) throw new Error('school_id requis pour uploader le logo')
    const formData = new FormData()
    formData.append('file', file)
    return apiService.post<any>(`/schools/${schoolId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  getPageVisits: async (params?: {
    user_id?: number
    page_category?: string
    visitor_type?: string
    device_type?: string
    country?: string
    city?: string
    date_from?: string
    date_to?: string
    search?: string
    page?: number
    per_page?: number
  }) => {
    return apiService.get<any>('/audit/page-visits', { params })
  },
}

// ============================================
// COMPÉTENCES (1er Cycle)
// ============================================
export const competenciesApi = {
  getAll: (params?: Record<string, any>) => apiService.get<any>('/competencies', { params }),
  getById: (id: number) => apiService.get<any>(`/competencies/${id}`),
  create: (data: any) => apiService.post<any>('/competencies', data),
  update: (id: number, data: any) => apiService.put<any>(`/competencies/${id}`, data),
  delete: (id: number) => apiService.delete<any>(`/competencies/${id}`),
  bulkCreate: (data: any) => apiService.post<any>('/competencies/bulk', data),
}

// ============================================
// BULLETINS (correction du prefixe)
// ============================================
export const bulletinsApi = {
  getStudentBulletin: (studentId: number, params?: any) => apiService.get<any>(`/bulletins/student/${studentId}`, { params }),
  getClassBulletins: (classId: number, params?: any) => apiService.get<any>(`/bulletins/class/${classId}`, { params }),
}

// ============================================
// REMPLACER l'ancien reportCardsApi par celui-ci (ou garder les deux)
// ============================================
export const reportCardsApi = {
  generate: (data: any) => apiService.post<any[]>('/report-cards/generate', data),
  getByStudent: (studentId: number, params?: any) => apiService.get<any>(`/students/${studentId}/report-cards`, { params }),
  getById: (id: number) => apiService.get<any>(`/report-cards/${id}`),
  bulkGenerate: (data: any) => apiService.post<Blob>('/report-cards/bulk-generate', data, { responseType: 'blob' }),
  print: (id: number) => apiService.get<Blob>(`/report-cards/${id}/print`, { responseType: 'blob' }),
  // Alias vers /bulletins pour compatibilité
  getBulletin: (studentId: number, params?: any) => apiService.get<any>(`/bulletins/student/${studentId}`, { params }),
  getClassBulletins: (classId: number, params?: any) => apiService.get<any>(`/bulletins/class/${classId}`, { params }),
}


// ============================================================
// CONTENU — BLOC FINAL À COLLER DANS src/services/api.ts
// Après collage : "export const contentApi" doit apparaître
// UNE SEULE FOIS dans le fichier (supprimez l'ancien bloc défectueux).
// ============================================================

// ─── ADMIN : gestion du contenu (auth requise) — utilisé par ContentPage.tsx ───
export const contentApi = {
  // Actualités
  getNews: (params?: any) => apiService.get<any>('/content/news', { params }),
  getNewsById: (id: number) => apiService.get<any>(`/content/news/${id}`),
  createNews: (data: any) => apiService.post<any>('/content/news', data),
  updateNews: (id: number, data: any) => apiService.put<any>(`/content/news/${id}`, data),
  deleteNews: (id: number) => apiService.delete(`/content/news/${id}`),

  // Événements
  getEvents: (params?: any) => apiService.get<any>('/content/events', { params }),
  getEventById: (id: number) => apiService.get<any>(`/content/events/${id}`),
  createEvent: (data: any) => apiService.post<any>('/content/events', data),
  updateEvent: (id: number, data: any) => apiService.put<any>(`/content/events/${id}`, data),
  deleteEvent: (id: number) => apiService.delete(`/content/events/${id}`),

  // Forum (chemins alignés backend : /content/forum/topics)
  getForumTopics: (params?: any) => apiService.get<any>('/content/forum/topics', { params }),
  getForumTopic: (id: number) => apiService.get<any>(`/content/forum/topics/${id}`),
  createForumTopic: (data: any) => apiService.post<any>('/content/forum/topics', data),
 
  deleteForumTopic: (id: number) => apiService.delete(`/content/forum/topics/${id}`),
  createForumReply: (topicId: number, data: any) =>
    apiService.post<any>(`/content/forum/topics/${topicId}/replies`, data),

  // Galerie
  getGallery: (params?: any) => apiService.get<any>('/content/gallery', { params }),
  uploadImage: (formData: FormData) =>
    apiService.post<any>('/content/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  deleteImage: (id: number) => apiService.delete(`/content/gallery/${id}`),
}

// ─── PUBLIQUE : contenu publié pour les visiteurs (SANS auth) ───
export const publicContentApi = {
  getNews: (params?: any) => apiService.get<any>('/content/public/news', { params }),
  getNewsById: (id: number) => apiService.get<any>(`/content/public/news/${id}`),
  likeNews: (id: number) => apiService.post<any>(`/content/public/news/${id}/like`, {}),

  getEvents: (params?: any) => apiService.get<any>('/content/public/events', { params }),
  getEventById: (id: number) => apiService.get<any>(`/content/public/events/${id}`),

  getForumTopics: (params?: any) => apiService.get<any>('/content/public/forum/topics', { params }),
  getForumTopic: (id: number) => apiService.get<any>(`/content/public/forum/topics/${id}`),
  replyToTopic: (id: number, data: { content: string; guest_name?: string }) =>
    apiService.post<any>(`/content/public/forum/topics/${id}/replies`, data),

  getGallery: (params?: any) => apiService.get<any>('/content/public/gallery', { params }),
   updateForumTopic: (id: number, data: any) => apiService.put<any>(`/content/forum/topics/${id}`, data),
}